import { ChevronDown } from "lucide-react";
import React from "react";

import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

import { ButtonNewVariant } from "./ButtonNew";

export interface SplitButtonItem {
	/** Rendered label of the menu item. */
	label: React.ReactNode;
	/** Optional lucide icon shown before the label. */
	icon?: React.ReactNode;
	onSelect: () => void;
	disabled?: boolean;
	/** Native tooltip on the item. */
	title?: string;
}

interface SplitButtonNewProps {
	/** Label of the primary segment. */
	children: React.ReactNode;
	/** Click handler of the primary segment. */
	onClick: () => void;
	/** Secondary actions behind the chevron. With none the chevron is left out. */
	items: SplitButtonItem[];
	variant?: ButtonNewVariant;
	/** Disables the primary segment only; the menu follows its items. */
	disabled?: boolean;
	className?: string;
	title?: string;
	/** Accessible name of the chevron segment. */
	menuLabel?: string;
	align?: "start" | "end";
}

const VARIANT_CLASSES: Record<
	ButtonNewVariant,
	{ group: string; segment: string; divider: string }
> = {
	primary: {
		group: "bg-brand text-brand-foreground",
		segment: "hover:bg-brand/90",
		divider: "border-brand-foreground/30",
	},
	danger: {
		group: "bg-danger text-white",
		segment: "hover:bg-danger/90",
		divider: "border-white/30",
	},
	dark: {
		group: "bg-neutral-800 text-white",
		segment: "hover:bg-neutral-700",
		divider: "border-white/20",
	},
	neutral: {
		group: "border border-solid border-gray-300 bg-white text-gray-700",
		segment: "hover:bg-gray-50",
		divider: "border-gray-300",
	},
};

/**
 * {@link ButtonNew} with a chevron segment that opens a menu of secondary
 * actions — the "Boka 1 pass ⌄" pattern of the Kandidater tab, generalised.
 * The primary segment is the one-click default; the menu holds the less
 * common alternatives (a dry run next to the real run, a variant of a save).
 *
 * The two segments are separate buttons sharing one rounded shell, so each
 * has its own disabled state: the chevron stays usable while every item is
 * enabled even if the primary action is not, and vice versa.
 */
const SplitButtonNew: React.FC<SplitButtonNewProps> = ({
	children,
	onClick,
	items,
	variant = "primary",
	disabled,
	className,
	title,
	menuLabel,
	align = "end",
}) => {
	const classes = VARIANT_CLASSES[variant];
	const menuDisabled = items.every((item) => item.disabled);

	const segmentClasses =
		"inline-flex h-full items-center justify-center gap-2 whitespace-nowrap border-0 bg-transparent text-sm font-medium text-inherit transition-colors " +
		"focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-400 " +
		"disabled:pointer-events-none disabled:opacity-50";

	return (
		<span
			className={cn(
				"inline-flex h-10 items-stretch overflow-hidden rounded-md",
				classes.group,
				className
			)}
		>
			<button
				type="button"
				onClick={onClick}
				disabled={disabled}
				title={title}
				className={cn(segmentClasses, classes.segment, "px-4")}
			>
				{children}
			</button>

			{items.length > 0 && (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<button
							type="button"
							disabled={menuDisabled}
							aria-label={menuLabel}
							title={menuLabel}
							className={cn(
								segmentClasses,
								classes.segment,
								"border-l border-solid px-2",
								classes.divider
							)}
						>
							<ChevronDown size={16} />
						</button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align={align} className="min-w-[10rem]">
						{items.map((item, index) => (
							<DropdownMenuItem
								key={index}
								disabled={item.disabled}
								title={item.title}
								onSelect={item.onSelect}
							>
								{item.icon}
								{item.label}
							</DropdownMenuItem>
						))}
					</DropdownMenuContent>
				</DropdownMenu>
			)}
		</span>
	);
};

export default SplitButtonNew;
